import { Injectable } from '@angular/core';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import type {
  MetadataResponse,
  MetadataUserDataSavedPlaceResponse,
  MetadataUserDataSettingsResponse,
} from '../../models/metadata-response';
import type { MaterialsResponse } from '../../models/quiz';
import type { SelectionResponse } from '../../models/selections';

export type OfflineBookStatus = 'downloading' | 'complete';

export interface OfflineBookRecord {
  loanKey: string;
  status: OfflineBookStatus;
  title: string;
  downloadedAt: number;
  loanEnd: number;
  /** Last time the book was opened; used to detect a clock set back. */
  lastSeenAt: number;
  /** Raw `GET /nubereader` response (manifest not deserialized) as it was downloaded. */
  metadata: MetadataResponse;
  /** Token of the URL stored in the extension; also the key to find/remove that bookmark. */
  bookmarkToken: string;
}

/**
 * The reader data that changes while reading, kept apart from the (large) metadata so a page turn
 * does not rewrite the whole manifest. Offline it already includes the changes not synced yet.
 */
export interface OfflineSnapshot {
  loanKey: string;
  selections: SelectionResponse[];
  savedPlace?: MetadataUserDataSavedPlaceResponse;
  settings: MetadataUserDataSettingsResponse;
  materials?: MaterialsResponse;
}

export type OutboxFormEntry =
  | { name: string; value: string }
  | { name: string; blob: Blob; filename?: string };

export type OutboxBody =
  | { kind: 'none' }
  | { kind: 'json'; value: unknown }
  | { kind: 'form'; entries: OutboxFormEntry[] };

/** A write made offline, waiting to be sent. */
export interface OutboxOp {
  seq?: number;
  loanKey: string;
  /** Sent as `Idempotency-Key`: a retried op is never applied twice. */
  opId: string;
  method: 'POST' | 'PUT' | 'DELETE';
  /** Path and query string. */
  url: string;
  body: OutboxBody;
  /** Temporary id of what this op creates, replaced by the server one once sent. */
  tempId?: string;
  /** Ops with the same key replace each other (only the last position or settings matter). */
  coalesceKey?: string;
  createdAt: number;
  /** Rejected by the server (4xx): kept for diagnosis, never sent again. */
  failedStatus?: number;
}

interface OfflineDbSchema extends DBSchema {
  books: { key: string; value: OfflineBookRecord };
  snapshots: { key: string; value: OfflineSnapshot };
  tokens: { key: string; value: { token: string; loanKey: string }; indexes: { byLoan: string } };
  outbox: { key: number; value: OutboxOp; indexes: { byLoan: string } };
}

const DB_NAME = 'nr-offline';
const DB_VERSION = 1;
let dbPromise: Promise<IDBPDatabase<OfflineDbSchema>> | undefined = undefined;

/** Shared connection; tests reset it with {@link resetOfflineDb}. */
export async function offlineDb(): Promise<IDBPDatabase<OfflineDbSchema>> {
  dbPromise ??= openDB<OfflineDbSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore('books', { keyPath: 'loanKey' });
      db.createObjectStore('snapshots', { keyPath: 'loanKey' });
      db.createObjectStore('tokens', { keyPath: 'token' }).createIndex('byLoan', 'loanKey');
      db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true }).createIndex(
        'byLoan',
        'loanKey',
      );
    },
  });
  return dbPromise;
}

export function resetOfflineDb(): void {
  dbPromise = undefined;
}

/** Download registry: what is stored offline and how to find it from a URL token. */
@Injectable({
  providedIn: 'root',
})
export class OfflineBookRegistry {
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async get(loanKey: string): Promise<OfflineBookRecord | undefined> {
    return (await offlineDb()).get('books', loanKey);
  }

  async findByToken(token: string): Promise<OfflineBookRecord | undefined> {
    if (!token) return undefined;
    const tokenRecord = await (await offlineDb()).get('tokens', token);
    return tokenRecord ? this.get(tokenRecord.loanKey) : undefined;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async put(record: OfflineBookRecord, tokens: string[] = []): Promise<void> {
    const tx = (await offlineDb()).transaction(['books', 'tokens'], 'readwrite');
    const tokenStore = tx.objectStore('tokens');
    await Promise.all([
      tx.objectStore('books').put(record),
      ...tokens
        .filter(Boolean)
        .map(async (token) => tokenStore.put({ token, loanKey: record.loanKey })),
      tx.done,
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async update(loanKey: string, changes: Partial<OfflineBookRecord>): Promise<void> {
    const tx = (await offlineDb()).transaction('books', 'readwrite');
    const record = await tx.store.get(loanKey);
    if (record) await tx.store.put({ ...record, ...changes, loanKey });
    await tx.done;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async addTokens(loanKey: string, tokens: string[]): Promise<void> {
    const tx = (await offlineDb()).transaction('tokens', 'readwrite');
    await Promise.all([
      ...tokens.filter(Boolean).map(async (token) => tx.store.put({ token, loanKey })),
      tx.done,
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async getSnapshot(loanKey: string): Promise<OfflineSnapshot | undefined> {
    return (await offlineDb()).get('snapshots', loanKey);
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async putSnapshot(snapshot: OfflineSnapshot): Promise<void> {
    await (await offlineDb()).put('snapshots', snapshot);
  }

  /** Removes the book with its snapshot, tokens and unsynced changes. */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  async delete(loanKey: string): Promise<void> {
    const tx = (await offlineDb()).transaction(
      ['books', 'snapshots', 'tokens', 'outbox'],
      'readwrite',
    );
    const tokenStore = tx.objectStore('tokens');
    const outboxStore = tx.objectStore('outbox');
    const [tokenKeys, opKeys] = await Promise.all([
      tokenStore.index('byLoan').getAllKeys(loanKey),
      outboxStore.index('byLoan').getAllKeys(loanKey),
    ]);
    await Promise.all([
      tx.objectStore('books').delete(loanKey),
      tx.objectStore('snapshots').delete(loanKey),
      ...tokenKeys.map(async (key) => tokenStore.delete(key)),
      ...opKeys.map(async (key) => outboxStore.delete(key)),
      tx.done,
    ]);
  }
}
